// 按鈕互動共用工具
// home / result 等 scene 的「按下縮小、放開還原並觸發 onClick、移出還原」是同一套樣板，
// 各自手寫 pointerdown/up/out 三段。抽成 attachPressFeedback 統一，差異用參數帶。
//
// === 使用圖片 ===
// 無 — 純事件 / 縮放邏輯，不建立任何顯示物件

import type Phaser from 'phaser';
import { animateTo, reducedMotion, Motion, type IMotionTarget } from './motion';
import { playUIClick } from '@audio/scene-audio';

export type TPressFeedbackOptions = {
  /** 常態縮放（圖片按鈕常非 1）。預設 1。 */
  readonly baseScale?: number;
  /** 按下時相對 baseScale 的縮放倍率（如 0.95）。 */
  readonly pressScale: number;
  /** 在按鈕上放開時觸發。 */
  readonly onClick: () => void;
  /** 按下瞬間的額外副作用（如暫停待機脈動）。 */
  readonly onPressStart?: () => void;
  /** 放開 / 移出時的額外副作用（如恢復待機脈動）。 */
  readonly onPressEnd?: () => void;
};

/**
 * 套用標準按鈕按壓回饋。
 *
 * - `hit`：接收指標事件的物件（呼叫端須已 setInteractive；start 鈕用獨立 Zone 當 hit）
 * - `scaleTarget`：實際縮放的視覺物件（通常同 hit；start 鈕用 Container）
 */
export const attachPressFeedback = (
  hit: Phaser.GameObjects.GameObject,
  scaleTarget: IMotionTarget,
  opts: TPressFeedbackOptions
): void => {
  const base = opts.baseScale ?? 1;
  let pressed = false;
  const release = (): void => {
    pressed = false;
    animateTo(hit.scene, scaleTarget, { scaleX: base, scaleY: base }, Motion.pressMs);
    opts.onPressEnd?.();
  };
  hit.on('pointerdown', () => {
    pressed = true;
    opts.onPressStart?.();
    const scale = reducedMotion() ? base : base * opts.pressScale;
    animateTo(hit.scene, scaleTarget, { scaleX: scale, scaleY: scale }, 0);
  });
  hit.on('pointerup', () => {
    if (!pressed) return;
    release();
    playUIClick(hit.scene);
    opts.onClick();
  });
  hit.on('pointerout', release);
  hit.on('pointerupoutside', release);
};
